import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Stack,
} from "@mui/material";
import config from "../config";

const SharedKycPage = () => {
  const { token: shareToken } = useParams();
  const navigate = useNavigate();
  const { API_BASE_URL } = config;

  const reduxIsAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState("");
  const [cookieChecked, setCookieChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [kycDetails, setKycDetails] = useState(null);
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Step 1: Check auth via /profile endpoint (cookie-based)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/v1/profile`, {
          withCredentials: true,
        });
        if (res.status === 200 && res.data?.user) {
          setIsAuthenticated(true);
          console.log("User authenticated via profile");
        } else {
          setIsAuthenticated(reduxIsAuthenticated || false);
        }
      } catch (err) {
        console.log("Profile check failed, checking Redux:", err.message);
        setIsAuthenticated(reduxIsAuthenticated || false);
      } finally {
        setCookieChecked(true);
      }
    };
    checkAuth();
  }, [API_BASE_URL, reduxIsAuthenticated]);

  // Step 2: Validate share token after auth check (ONLY ONCE)
  useEffect(() => {
    if (!cookieChecked || validationAttempted) return;

    if (!shareToken) {
      setError("Invalid share link.");
      setLoading(false);
      setValidationAttempted(true);
      return;
    }

    const validateShareToken = async () => {
      try {
        setLoading(true);
        console.log("Validating share token...");
        
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/kyc/validate-token`,
          { token: shareToken },
          { withCredentials: true }
        );

        console.log("Token validation response:", response.data);

        // Check if validation was successful
        if (response.data?.success === false) {
          setError(
            response.data?.message || "This share link is invalid or has expired."
          );
          setKycDetails(null);
          setValidationAttempted(true);
          setLoading(false);
          return;
        }

        if (response.data?.success === true) {
          const validationData = response.data?.data || response.data;

          // Handle different token statuses
          if (validationData?.status === "used") {
            setError("This share link has already been used and is no longer available.");
            setKycDetails(null);
            setValidationAttempted(true);
            setLoading(false);
            return;
          }

          if (validationData?.status === "expired") {
            setError("This share link has expired.");
            setKycDetails(null);
            setValidationAttempted(true);
            setLoading(false);
            return;
          }

          // Token is valid, now fetch the actual KYC document
          try {
            console.log("Fetching KYC document...");
            const kycResponse = await axios.get(
              `${API_BASE_URL}/api/v1/kyc/access/${shareToken}`,
              { withCredentials: true }
            );

            console.log("KYC Document fetched:", kycResponse.data);
            
            setKycDetails({
              ...validationData,
              kyc: kycResponse.data?.kyc,
              owner: kycResponse.data?.owner,
            });
            setError("");
          } catch (docErr) {
            console.error("Error fetching KYC document:", docErr);
            // Even if document fetch fails, still show the token details
            setKycDetails(validationData);
            setError("");
          }
        } else {
          setError("Unable to validate this share link.");
          setKycDetails(null);
        }
      } catch (err) {
        console.error("Error validating token:", err);
        setError(
          err.response?.data?.message ||
            "This share link is invalid or has expired."
        );
        setKycDetails(null);
      } finally {
        setValidationAttempted(true);
        setLoading(false);
      }
    };

    validateShareToken();
  }, [shareToken, API_BASE_URL, cookieChecked, validationAttempted]);

  // Step 3: Accept and navigate to KYC view
  const handleAcceptAccess = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    setOutcome("KYC access granted successfully!");

    // Redirect to KYC view page after 2 seconds
    setTimeout(() => {
      if (kycDetails?.kyc?._id) {
        navigate(`/kyc/${kycDetails.kyc._id}`);
      } else {
        navigate("/dashboard");
      }
    }, 2000);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Processing your KYC access...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Alert severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            sx={{ mt: 2 }}
          >
            Go to Home
          </Button>
        </Box>
      );
    }

    if (outcome) return <Alert severity="success">{outcome}</Alert>;

    if (kycDetails) {
      return (
        <>
          <Typography variant="h5" gutterBottom>
            KYC Document Access
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You have been granted access to a KYC document.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "grey.100" }}>
            {kycDetails.kyc?.companyInfo?.legalName && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Company:</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {kycDetails.kyc.companyInfo.legalName}
                </Typography>
              </>
            )}

            {kycDetails.owner?.email && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Shared By:</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {kycDetails.owner.email}
                </Typography>
              </>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              <strong>Access Level:</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {kycDetails.accessLevel === "edit"
                ? "View & Edit"
                : "View Only"}
            </Typography>

            {kycDetails.expiresAt && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Expires:</strong>
                </Typography>
                <Typography variant="body1">
                  {new Date(kycDetails.expiresAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </>
            )}
          </Paper>

          {!isAuthenticated ? (
            <Box sx={{ my: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please log in to access this document.
              </Alert>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `/login?redirect=${encodeURIComponent(window.location.pathname)}`
                  )
                }
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleAcceptAccess}
              >
                Accept & View KYC
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/")}
              >
                Cancel
              </Button>
            </Box>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: "500px", width: "100%" }}>
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default SharedKycPage;